// views/device/DeviceListView.tsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  getDeviceListApi,
  searchDevicesApi,
  deleteDeviceApi,
  downloadDevicesExcelApi,
  getDeviceStatsApi,
  getRegionsApi,
  getDeviceDetailApi,
} from "@/lib/api";
import { useLoading } from "@/store/loadingStore";
import { useModal } from "@/store/modalStore";
import { DeviceResponseType, PageResponse, RegionResponseType, DeviceStatsResponseType, DeviceDetailResponseType } from "@/types";
import { regions, getRegionByName } from "@/constants/regions";
import "./DeviceList.css";

const DeviceListView = () => {
  const navigate = useNavigate();
  const { startLoading, stopLoading } = useLoading();
  const modal = useModal();

  // 상태 관리
  const [devices, setDevices] = useState<DeviceResponseType[]>([]);
  const [selectedDevices, setSelectedDevices] = useState<number[]>([]);
  const [selectedProvince, setSelectedProvince] = useState("전라남도");
  const [selectedCity, setSelectedCity] = useState("나주시");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize] = useState(10);

  // 선택된 기기 정보
  const [selectedDevice, setSelectedDevice] = useState<DeviceDetailResponseType | null>(null);

  // 지역 정보
  const [availableProvinces, setAvailableProvinces] = useState<string[]>([]);
  const [availableCities, setAvailableCities] = useState<string[]>([]);
  const [availableDistricts, setAvailableDistricts] = useState<string[]>([]);

  // 통계 정보
  const [stats, setStats] = useState<DeviceStatsResponseType | null>(null);

  // 컴포넌트 마운트 시 지역 정보 초기화
  useEffect(() => {
    initializeRegions();
    loadDevices();
    loadStats();
  }, []);

  // 필터 변경 시 데이터 다시 로드
  useEffect(() => {
    loadDevices();
  }, [currentPage, statusFilter, selectedProvince, selectedCity, selectedDistrict]);

  // 지역 정보 초기화
  const initializeRegions = () => {
    // regions.ts에서 고유한 도/시 정보 추출
    const uniqueProvinces = [...new Set(regions.map(r => r.fullName.split(' ')[0]))];
    setAvailableProvinces(uniqueProvinces);

    // 전라남도 선택 시 시/군 정보 추출
    updateCities("전라남도");
    updateDistricts("전라남도", "나주시");
  };

  // 도 선택 시 시/군 정보 업데이트
  const updateCities = (province: string) => {
    const cities = regions
      .filter(r => r.fullName.includes(province))
      .map(r => {
        const parts = r.fullName.split(' ');
        return parts.length > 1 ? parts[1] : '';
      })
      .filter(city => city && !city.includes('면') && !city.includes('동'))
      .filter((city, index, arr) => arr.indexOf(city) === index);
    
    setAvailableCities(cities);
  };

  // 시/군 선택 시 읍/면/동 정보 업데이트
  const updateDistricts = (province: string, city: string) => {
    const districts = regions
      .filter(r => r.fullName.includes(province) && r.fullName.includes(city))
      .map(r => {
        const parts = r.fullName.split(' ');
        return parts.length > 2 ? parts[2] : '';
      })
      .filter(district => district)
      .filter((district, index, arr) => arr.indexOf(district) === index);
    
    setAvailableDistricts(districts);
  };

  const loadDevices = async () => {
    try {
      startLoading("기기 목록을 불러오는 중...");

      // 지역 코드 찾기 - 읍/면/동이 선택된 경우
      let regionCode = null;
      if (selectedDistrict) {
        const selectedRegion = getRegionByName(`전라남도 ${selectedCity} ${selectedDistrict}`);
        regionCode = selectedRegion?.code;
        console.log("선택된 지역:", `전라남도 ${selectedCity} ${selectedDistrict}`, "지역 코드:", regionCode);
      }

      // 필터 조건 설정
      const filters: any = {};
      if (statusFilter) {
        filters.status = statusFilter;
        console.log("상태 필터:", statusFilter);
      }
      if (regionCode) {
        filters.regionCode = regionCode;
        console.log("지역 필터:", regionCode);
      }

      console.log("API 호출 필터:", filters);

      const response: PageResponse<DeviceResponseType> = await getDeviceListApi(
        currentPage,
        pageSize,
        filters
      );

      if (response?.content) {
        setDevices(response.content);
        setTotalPages(response.totalPages || 0);
        setTotalElements(response.totalElements || 0);
      } else {
        setDevices([]);
        setTotalPages(0);
        setTotalElements(0);
      }
    } catch (error) {
      console.error("기기 목록 조회 실패:", error);
      // 더 구체적인 에러 메시지 제공
      let errorMessage = "기기 목록을 불러오는데 실패했습니다.";
      if (error?.status === 401) {
        errorMessage = "인증이 만료되었습니다. 다시 로그인해주세요.";
      } else if (error?.status === 403) {
        errorMessage = "권한이 없습니다. 관리자에게 문의하세요.";
      } else if (error?.status === 500) {
        errorMessage = "서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.";
      } else if (error?.code === 'NETWORK_ERROR') {
        errorMessage = "네트워크 연결을 확인해주세요.";
      }
      modal.alert("오류", errorMessage);
      
      // 오류 발생 시 빈 목록으로 초기화
      setDevices([]);
      setTotalPages(0);
      setTotalElements(0);
    } finally {
      stopLoading();
    }
  };

  const loadStats = async () => {
    try {
      const statsResponse = await getDeviceStatsApi();
      setStats(statsResponse);
    } catch (error) {
      console.error("통계 정보 로드 실패:", error);
      // 오류 시 기본값 설정
      setStats({
        totalDevices: 130,
        activeDevices: 24,
        inactiveDevices: 6,
        maintenanceDevices: 0,
        devicesByRegion: [],
        recentInstallations: []
      });
    }
  };

  // 검색
  const handleSearch = async () => {
    if (!searchKeyword.trim()) {
      loadDevices();
      return;
    }

    try {
      startLoading("검색 중...");
      const response = await searchDevicesApi(searchKeyword, currentPage, pageSize);
      setDevices(response.content);
      setTotalPages(response.totalPages);
      setTotalElements(response.totalElements);
    } catch (error) {
      modal.alert("오류", "검색 중 오류가 발생했습니다.");
    } finally {
      stopLoading();
    }
  };

  // 전체 선택/해제
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedDevices(devices.map((device) => device.id));
    } else {
      setSelectedDevices([]);
    }
  };

  // 개별 선택
  const handleSelectDevice = (deviceId: number, checked: boolean) => {
    if (checked) {
      setSelectedDevices([...selectedDevices, deviceId]);
    } else {
      setSelectedDevices(selectedDevices.filter((id) => id !== deviceId));
    }
  };

  // 행 클릭시 상세 정보 표시
  const handleRowClick = async (device: DeviceResponseType) => {
    try {
      startLoading("기기 상세 정보를 불러오는 중...");
      const deviceDetail = await getDeviceDetailApi(device.id);
      setSelectedDevice(deviceDetail);
    } catch (error) {
      console.error('상세 정보 로드 실패:', error);
      // 더 구체적인 에러 메시지 제공
      let errorMessage = "상세 정보를 불러오는데 실패했습니다.";
      if (error?.status === 401) {
        errorMessage = "인증이 만료되었습니다. 다시 로그인해주세요.";
      } else if (error?.status === 404) {
        errorMessage = "해당 기기 정보를 찾을 수 없습니다.";
      } else if (error?.status === 500) {
        errorMessage = "서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.";
      }
      modal.alert("오류", errorMessage);
    } finally {
      stopLoading();
    }
  };

  // 삭제
  const handleDelete = async () => {
    if (selectedDevices.length === 0) {
      modal.alert("알림", "삭제할 기기를 선택해주세요.");
      return;
    }

    modal.confirm(
      "삭제 확인",
      `선택한 ${selectedDevices.length}개의 기기를 삭제하시겠습니까?`,
      async () => {
        try {
          startLoading("삭제 중...");

          // 선택된 기기들 삭제
          await Promise.all(selectedDevices.map((id) => deleteDeviceApi(id)));

          modal.alert("완료", "기기가 삭제되었습니다.");
          setSelectedDevices([]);
          loadDevices();
        } catch (error) {
          modal.alert("오류", "삭제 중 오류가 발생했습니다.");
        } finally {
          stopLoading();
        }
      }
    );
  };

  // 지역 선택 핸들러
  const handleProvinceChange = (province: string) => {
    setSelectedProvince(province);
    updateCities(province);
    setSelectedCity('');
    setSelectedDistrict('');
    setCurrentPage(0);
  };

  const handleCityChange = (city: string) => {
    setSelectedCity(city);
    updateDistricts(selectedProvince, city);
    setSelectedDistrict('');
    setCurrentPage(0);
  };

  const handleDistrictChange = (district: string) => {
    setSelectedDistrict(district);
    setCurrentPage(0);
  };

  // 엑셀 다운로드
  const handleExcelDownload = async () => {
    try {
      startLoading("엑셀 파일 생성 중...");
      
      // 현재 선택된 필터를 다운로드에 적용
      let regionCode = null;
      if (selectedDistrict) {
        const selectedRegion = getRegionByName(`전라남도 ${selectedCity} ${selectedDistrict}`);
        regionCode = selectedRegion?.code;
      }

      const filters: any = {};
      if (statusFilter) filters.status = statusFilter;
      if (regionCode) filters.regionCode = regionCode;

      await downloadDevicesExcelApi(filters);
    } catch (error) {
      modal.alert("오류", "엑셀 다운로드 중 오류가 발생했습니다.");
    } finally {
      stopLoading();
    }
  };

  // 수정 페이지로 이동
  const handleEdit = (deviceId: number) => {
    navigate(`/device/edit/${deviceId}`);
  };

  // 등록 페이지로 이동
  const handleRegister = () => {
    navigate("/device/register");
  };

  return (
    <div className="device-list-container">
      {/* 탭 버튼 */}
      <div className="tab-buttons">
        <div className="" style={{ float: "left" }}>
          <button className="tab-button active">기기 관리</button>
          <button className="tab-button" onClick={handleRegister}>
            신규등록
          </button>
        </div>
        <a
          href={"javascript:;"}
          className="btn-excel-download"
          onClick={handleExcelDownload}
          style={{ float: "right" }}
        >
          <img src={"/img/btn_excel01.png"} />
        </a>
      </div>
      <div className="dlv_dv01">
        {/* 검색 영역 */}
        <div className="search-top">
          <div className="search-section">
            <select 
              className="search-type" 
              value={selectedProvince}
              onChange={(e) => handleProvinceChange(e.target.value)}
            >
              {availableProvinces.map(province => (
                <option key={province} value={province}>{province}</option>
              ))}
            </select>
            <select 
              className="search-type" 
              value={selectedCity}
              onChange={(e) => handleCityChange(e.target.value)}
            >
              <option value="">시/군 선택</option>
              {availableCities.map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
            <select
              className="search-type"
              value={selectedDistrict}
              onChange={(e) => handleDistrictChange(e.target.value)}
            >
              <option value="">읍/면/동 선택</option>
              {availableDistricts.map(district => (
                <option key={district} value={district}>{district}</option>
              ))}
            </select>
            <select
              className="status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">전체 상태</option>
              <option value="ACTIVE">운영중</option>
              <option value="INACTIVE">미운영</option>
              <option value="MAINTENANCE">점검중</option>
            </select>

            <button className="btn-search" onClick={loadDevices}>
              조회
            </button>
          </div>

          {/* 통계 정보 */}
          <div className="stats-section">
            <span>
              모든장치 <strong>{stats?.totalDevices || 0}</strong>
            </span>
            <span className="divider">|</span>
            <span>
              온라인 <strong className="text-green">{stats?.activeDevices || 0}</strong>
            </span>
            <span className="divider">|</span>
            <span>
              오프라인 <strong className="text-red">{(stats?.inactiveDevices || 0) + (stats?.maintenanceDevices || 0)}</strong>
            </span>
          </div>
        </div>

        {/* 테이블 */}
        <div className="table-section">
          <table className="device-table">
            <thead>
              <tr>
                {/*<th>*/}
                {/*  <input*/}
                {/*    type="checkbox"*/}
                {/*    onChange={(e) => handleSelectAll(e.target.checked)}*/}
                {/*    checked={selectedDevices.length === devices.length && devices.length > 0}*/}
                {/*  />*/}
                {/*</th>*/}
                <th>아이디</th>
                <th>브랜드</th>
                <th>설치주소</th>
                <th>구매처</th>
                <th>구입일</th>
                <th>판매자</th>
                <th>판매자연락처</th>
                <th>판매자이메일</th>
                <th>활성화</th>
              </tr>
            </thead>
            <tbody>
              {devices.map((device) => (
                <tr
                  key={device.id}
                  className={selectedDevice?.id === device.id ? "selected" : ""}
                  onClick={() => handleRowClick(device)}
                >
                  {/*<td onClick={(e) => e.stopPropagation()}>*/}
                  {/*  <input*/}
                  {/*    type="checkbox"*/}
                  {/*    checked={selectedDevices.includes(device.id)}*/}
                  {/*    onChange={(e) => handleSelectDevice(device.id, e.target.checked)}*/}
                  {/*  />*/}
                  {/*</td>*/}
                  <td>{device.deviceId}</td>
                  <td>{device.brand || "-"}</td>
                  <td>{device.regionFullName}</td>
                  <td>{device.purchaseLocation || "-"}</td>
                  <td>{device.purchaser || "-"}</td>
                  <td>{device.sellerName || "-"}</td>
                  <td>{device.sellerContact || "-"}</td>
                  <td>{device.sellerEmail || "-"}</td>
                  <td>
                    <span className={`status ${device.status === "ACTIVE" ? "on" : "off"}`}>
                      {device.status === "ACTIVE" ? "ON" : "OFF"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 페이지네이션 */}
        <div className="pagination">
          <button onClick={() => setCurrentPage(0)} disabled={currentPage === 0}>
            처음
          </button>
          <button onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 0}>
            이전
          </button>

          <span className="page-info">
            {currentPage + 1} / {totalPages || 1}
          </span>

          <button
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage >= totalPages - 1}
          >
            다음
          </button>
          <button
            onClick={() => setCurrentPage(totalPages - 1)}
            disabled={currentPage >= totalPages - 1}
          >
            마지막
          </button>
        </div>
      </div>
      {/* 선택된 기기 상세 정보 */}
      {selectedDevice && (
        <div className="device-detail-section">

          <div className="detail-content">
            <div className="detail-header">
              <h3>기기정보</h3>
              <span className="status-text">
              활성화 상태{" "}
                <span className="status-value">
                {selectedDevice.status === "ACTIVE" ? "ON" : "OFF"}
              </span>
            </span>
              <div className="detail-actions">
                <button className="btn-modify" onClick={() => handleEdit(selectedDevice.id)}>
                  수정
                </button>
                <button className="btn-delete-detail">삭제</button>
              </div>
            </div>
            <table className="detail-table">
              <tbody>
                <tr>
                  <td className="label">아이디</td>
                  <td className="value">{selectedDevice.deviceId}</td>
                  <td className="label">브랜드</td>
                  <td className="value">{selectedDevice?.brand || "-"}</td>
                </tr>
                <tr>
                  <td className="label">기기명</td>
                  <td className="value">{selectedDevice?.deviceName || "-"}</td>
                  <td className="label">측정방식</td>
                  <td className="value">{selectedDevice?.measurementMethod || "-"}</td>
                </tr>
                <tr>
                  <td className="label">기기모델</td>
                  <td className="value">{selectedDevice?.deviceModel || "-"}</td>
                  <td className="label">측정항목</td>
                  <td className="value">{selectedDevice?.measurementItems || "-"}</td>
                </tr>
                <tr>
                  <td className="label">사업장위치</td>
                  <td className="value">
                    {selectedDevice.regionFullName}
                  </td>
                  <td className="label">구매자</td>
                  <td className="value">{selectedDevice?.purchaser || "-"}</td>
                </tr>
                <tr>
                  <td className="label">상세위치</td>
                  <td className="value">농장 깃발아래</td>
                  <td className="label">구매처</td>
                  <td className="value">웰케어</td>
                </tr>
                <tr>
                  <td className="label">위도</td>
                  <td className="value">{selectedDevice?.installation?.latitude || "-"}</td>
                  <td className="label">구매처연락처</td>
                  <td className="value">{selectedDevice?.sellerContact || "-"}</td>
                </tr>
                <tr>
                  <td className="label">경도</td>
                  <td className="value">{selectedDevice?.installation?.longitude || "-"}</td>
                  <td className="label">판매자이메일</td>
                  <td className="value">{selectedDevice?.sellerEmail || "-"}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="detail-content2">
            <div className="detail-header">
              <h3>점검관리</h3>
              <div className="detail-actions">
                <button className="btn-save">저장</button>
              </div>
            </div>
            <div className="detail-edit">
              <textarea className="detail-edit-ta">2025-03-29 신규로 설치함</textarea>
            </div>
          </div>
        </div>
      )}

      {/* 하단 액션 버튼 */}
      {/*<div className="bottom-actions">*/}
      {/*  <button className="btn-delete" onClick={handleDelete}>*/}
      {/*    삭제*/}
      {/*  </button>*/}
      {/*  <button className="btn-register" onClick={handleRegister}>*/}
      {/*    등록*/}
      {/*  </button>*/}
      {/*</div>*/}
    </div>
  );
};

export default DeviceListView;
